import { CustomFieldClassType } from 'shared'
import { HelpMessage } from 'utils/constants'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import { useUsersAndRolesColumn } from 'components/CIMSTable/common/hooks/useUsersAndRolesColumn'
import { useCustomFieldPageContext } from '../../../CustomFieldPageContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DefaultMutationHookOptions } from 'utils/defaults'
import {
  CustomField,
  EditCustomFieldToClassTypeInput,
  useEditCustomFieldToClassTypeMutation,
} from 'schema'
import CIMSCheckbox from 'components/CIMSCheckbox'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'
import { ProjectCustomField } from 'components/CustomFields/ProjectCustomFieldsContext/types'
import { DisabledOptions } from 'hooks/useDisabledOptions'

type CustomFieldToClassType =
  ProjectCustomField['customFieldToClassTypes'][0] & {
    customField: Pick<CustomField, 'deleted' | 'id'>
  }

export function useColumns() {
  const help = HelpMessage.Administration.CustomFields
  const { edit: editCustomField } = useCustomFieldPageContext()
  const { refetch } = useProjectCustomFieldsContext()
  const [edit, { loading: editing }] = useEditCustomFieldToClassTypeMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editCustomFieldToClassType: {
        customFieldToClassType: {
          classType,
          customField: { name },
        },
      },
    }) => simpleEditNotification(`${name} for ${classType} records`),
  })
  const [keyedLoading, { add, remove }] = useKeyedBooleans()
  const canEdit = editCustomField.can
  const check = async (
    record: CustomFieldToClassType,
    key: Exclude<
      keyof CustomFieldToClassType & keyof EditCustomFieldToClassTypeInput,
      'classTypeId'
    >
  ) => {
    if (!canEdit) return
    const { classTypeId, customField } = record
    add(`${classTypeId}-${key}`)
    await edit({
      variables: {
        editCustomFieldToClassTypeInput: {
          classTypeId,
          customFieldId: customField.id,
          [key]: !record[key],
        },
      },
    })
    await refetch()
    remove(`${classTypeId}-${key}`)
  }
  const commonDisabledOptions = (
    record: CustomFieldToClassType
  ): DisabledOptions[] => [
    {
      disabled: !canEdit,
      reason: 'missing permission',
    },
    {
      disabled: record.customField.deleted,
      reason: {
        why: 'inactive',
        entityName: 'Custom Field',
      },
    },
  ]
  const columns: CIMSColumn<CustomFieldToClassType>[] = [
    {
      title: 'Type',
      help: help.ClassTypes({ header: false }),
      render: ({ classTypeId }) =>
        CustomFieldClassType.fromIdOrFail(classTypeId).type,
      width: 200,
    },
    useUsersAndRolesColumn({
      help: HelpMessage.Administration.CustomFields.Editors,
      title: 'Editors',
    }),
    {
      title: 'Always Visible',
      align: 'center',
      help: HelpMessage.Administration.CustomFields.AlwaysVisible,
      width: 142,
      render: (record) => {
        return (
          <CIMSCheckbox
            checked={record.alwaysVisible}
            loading={keyedLoading(`${record.classTypeId}-alwaysVisible`)}
            disabled={[
              {
                disabled: !canEdit,
                reason: 'missing permission',
              },
              {
                disabled: !record.customField.deleted,
                reason: {
                  why: 'disabled',
                  message: `Always Visible is only applicable to custom fields which 
                    have been removed/are inactive`,
                },
              },
            ]}
            onChange={() => check(record, 'alwaysVisible')}
          />
        )
      },
    },
    {
      title: 'Always Editable',
      align: 'center',
      width: 152,
      help: HelpMessage.Administration.CustomFields.AlwaysEditable,
      render: (record) => (
        <CIMSCheckbox
          disabled={commonDisabledOptions(record)}
          checked={record.alwaysEditable}
          loading={keyedLoading(`${record.classTypeId}-alwaysEditable`)}
          onChange={() => check(record, 'alwaysEditable')}
        />
      ),
    },
    {
      title: 'Required',
      align: 'center',
      width: 111,
      help: HelpMessage.Administration.CustomFields.Required,
      render: (record) => (
        <CIMSCheckbox
          disabled={commonDisabledOptions(record)}
          checked={record.required}
          loading={keyedLoading(`${record.classTypeId}-required`)}
          onChange={() => check(record, 'required')}
        />
      ),
    },
    {
      title: 'Prominent',
      align: 'center',
      width: 118,
      help: HelpMessage.Administration.CustomFields.Prominent,
      render: (record) => (
        <CIMSCheckbox
          disabled={commonDisabledOptions(record)}
          checked={record.prominent}
          loading={keyedLoading(`${record.classTypeId}-prominent`)}
          onChange={() => check(record, 'prominent')}
        />
      ),
    },
  ]

  return { columns, editing }
}
